import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import { Eyebrow, Pill } from '../Vitrine/VitrineUi';
import { VitrineLayout, useDemoButton } from '../Vitrine/VitrineLayout';
import { BlocPilote, TITRE_SECTION } from '../Vitrine/ModulesEtFormules';
import { ACCOMPAGNEMENT, DOCUMENTS, STATS } from '../Vitrine/vitrineData';
import { PROFILS_EXEMPLES } from '../Vitrine/vitrineAccueil';

const COUCHES = `
    .carte-vivante { transition: transform .18s ease, border-color .18s ease, background-color .18s ease; }
    .carte-vivante:hover { transform: translateY(-3px); }
    .carte-vivante:focus-visible { outline: 2px solid var(--acc); outline-offset: 2px; }
    @keyframes accueil-bulle { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    .bulle { animation: accueil-bulle .5s ease both; }
    @media (prefers-reduced-motion: reduce) {
        .carte-vivante, .carte-vivante:hover { transition: none; transform: none; }
        .bulle { animation: none; }
    }
`;

const PHRASES_METIERS: Record<string, string> = {
    restaurateur: 'Planning, réservations en ligne, renforts, fournisseurs, privatisations.',
    prestataire: 'Devis en ligne, rendez-vous, tournée, facture : plombier, coiffeuse, prothésiste ongulaire…',
    fournisseur: 'Catalogue, prix par client, commandes reçues : fruits et légumes, hygiène, boissons…',
};

const VIGNETTES: Record<string, string> = { restaurateur: '/vitrine/livre-reservations.png', prestataire: '/vitrine/agenda-prestataire.png', fournisseur: '/vitrine/catalogue-fournisseur.png' };

const Accueil: React.FC = () => {
    const { tokens } = useAmbiance();
    const demoButton = useDemoButton();

    return (
        <>
            <style>{COUCHES}</style>
            <div style={{ ['--acc' as string]: tokens.acc }}>
                <AmbianceBand veil="linear-gradient(180deg, rgba(5,6,10,.30) 0%, rgba(5,6,10,.18) 60%, rgba(5,6,10,.45) 100%)">
                    <section className="max-w-[1160px] mx-auto px-5 pt-16 md:pt-20 text-center">
                        <h1 className="mx-auto max-w-[820px] text-[38px] md:text-[64px] font-semibold leading-[1.04] tracking-[-0.02em] mb-5">
                            Activez vos modules. On code le reste.
                        </h1>
                        <p className="mx-auto max-w-[620px] text-[16px] md:text-[17px] text-content-muted leading-relaxed mb-7">
                            La plateforme des restaurants, des hôtels, des prestataires de service et de leurs fournisseurs. Vous n'allumez que ce qui vous sert.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            {demoButton('lg')}
                            <Pill variant="secondary" size="lg" to="/formules">Voir les formules</Pill>
                        </div>
                        <div className="relative mx-auto mt-12 max-w-[980px]">
                            <div className="rounded-t-[14px] border border-b-0 border-border bg-surface overflow-hidden shadow-2xl">
                                <div className="flex items-center gap-1.5 h-9 px-3 border-b border-border bg-surface-soft">
                                    <span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" />
                                    <span className="ml-3 text-[11px] font-mono text-content-muted">l-iamani.com/reservations</span>
                                </div>
                                <img src="/vitrine/livre-reservations.png" alt="Livre de réservations du samedi soir : 24 réservations, 81 couverts" className="block w-full h-auto" />
                            </div>
                        </div>
                    </section>
                </AmbianceBand>

                <section className="border-y border-border bg-surface">
                    <div className="max-w-[1160px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
                        {STATS.map((s) => (
                            <div key={s.label} className="py-6 md:px-6 first:pl-0">
                                <div className="text-[28px] font-bold tracking-tight leading-none">{s.value}</div>
                                <div className="mt-2 text-[13px] text-content-muted">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Quatre portes d'entrée, une page par métier */}
                <section className="max-w-[1160px] mx-auto px-5 py-16 md:py-20">
                    <Eyebrow>Votre métier</Eyebrow>
                    <h2 className={`max-w-[640px] ${TITRE_SECTION} mb-3`}>Choisissez le vôtre. Une page, un exemple, des captures.</h2>
                    <p className="max-w-[560px] text-[14.5px] text-content-muted mb-8">Chaque métier a sa page : une journée type chez un établissement d'exemple, les modules utilisés et ce que vous verrez à l'écran.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {PROFILS_EXEMPLES.map((p) => (
                            <Link key={p.cle} to={`/metiers/${p.cle}`} className="carte-vivante group rounded-2xl border border-border bg-surface p-6 flex flex-col hover:border-content">
                                <span className="block -mx-6 -mt-6 mb-5 h-[150px] overflow-hidden rounded-t-2xl border-b border-border bg-surface-soft"><img src={VIGNETTES[p.cle]} alt="" className="w-full h-full object-cover object-top" loading="lazy" /></span>
                                <span className="text-[10px] font-bold uppercase tracking-[.09em] mb-3" style={{ color: tokens.acc }}>{p.onglet}</span>
                                <span className="text-[22px] font-semibold leading-tight mb-2">{p.nom}</span>
                                <span className="text-[13.5px] text-content-muted leading-relaxed mb-5">{PHRASES_METIERS[p.cle]}</span>
                                <span className="mt-auto inline-flex items-center gap-1.5 text-[14px] font-semibold">Voir cette page <ArrowRight size={15} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" /></span>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Concrètement : trois documents */}
                <section className="max-w-[1160px] mx-auto px-5 pb-16 md:pb-20">
                    <Eyebrow>Concrètement</Eyebrow>
                    <h2 className={`max-w-[560px] ${TITRE_SECTION} mb-8`}>Trois documents que vous ne ferez plus à la main.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {DOCUMENTS.map((d) => (
                            <article key={d.title} className="carte-vivante rounded-xl border border-border bg-surface overflow-hidden">
                                <div className="aspect-[16/10] bg-surface-soft p-3 flex items-center justify-center"><img src={d.src} alt={d.alt} className="max-w-full max-h-full object-contain rounded-md shadow-sm" loading="lazy" /></div>
                                <div className="p-5"><h3 className="text-[15px] font-semibold mb-1">{d.title}</h3><p className="text-[13px] text-content-muted leading-relaxed">{d.desc}</p></div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* Conseillère IA */}
                <section id="conseillere" className="bg-surface-app border-y border-border">
                    <div className="max-w-[1160px] mx-auto px-5 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <div>
                            <Eyebrow>Conseillère IA</Eyebrow>
                            <h2 className={`${TITRE_SECTION} mb-4`}>Elle répond à vos clients. Vous reprenez la main quand vous voulez.</h2>
                            <p className="text-[14.5px] text-content-muted leading-relaxed mb-4">Elle connaît votre carte, vos horaires et vos formules. Elle renseigne, propose un rendez-vous ou une réservation, puis passe la conversation à votre équipe quand la question dépasse son rôle.</p>
                            <ul className="space-y-2 text-[14px]">
                                {['Elle ne donne que des informations de votre espace', 'Vous voyez chaque conversation et pouvez répondre à sa place', 'Incluse dans la formule Croître'].map((t) => (
                                    <li key={t} className="flex gap-2"><Check size={16} className="mt-0.5 shrink-0" style={{ color: tokens.acc }} aria-hidden="true" />{t}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="rounded-2xl border border-border bg-surface p-5 shadow-xl" role="img" aria-label="Exemple de conversation : un client demande s'il reste une table samedi soir, la Conseillère IA propose deux horaires puis passe la main à l'équipe.">
                            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-border">
                                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: tokens.acc }}><Sparkles size={15} aria-hidden="true" /></span>
                                <div><div className="text-[13.5px] font-semibold leading-none">Conseillère</div><div className="text-[11px] text-content-muted mt-1">Réponse en quelques secondes</div></div>
                            </div>
                            <div className="space-y-2.5" aria-hidden="true">
                                <div className="bulle max-w-[82%] rounded-2xl rounded-bl-sm bg-surface-soft border border-border px-3.5 py-2.5 text-[13px]">Bonjour, il reste une table pour 4 samedi soir ?</div>
                                <div className="bulle max-w-[82%] ml-auto rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[13px] text-white" style={{ background: tokens.acc, animationDelay: '.5s' }}>Oui ! Il me reste 19:30 ou 21:00. Laquelle préférez-vous ?</div>
                                <div className="bulle max-w-[82%] rounded-2xl rounded-bl-sm bg-surface-soft border border-border px-3.5 py-2.5 text-[13px]" style={{ animationDelay: '1s' }}>19:30. On a un enfant allergique aux arachides.</div>
                                <div className="bulle max-w-[82%] ml-auto rounded-2xl rounded-br-sm px-3.5 py-2.5 text-[13px] text-white" style={{ background: tokens.acc, animationDelay: '1.5s' }}>Noté. Je transmets à l'équipe pour confirmer l'allergie en cuisine.</div>
                                <div className="bulle text-center text-[11.5px] text-content-muted pt-1" style={{ animationDelay: '2s' }}>— Conversation passée à votre équipe —</div>
                            </div>
                        </div>
                    </div>
                </section>

                <BlocPilote />

                <section id="accompagnement" className="max-w-[1160px] mx-auto px-5 pb-16 md:pb-20 scroll-mt-32">
                    <div className="rounded-2xl border border-border bg-surface p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <Eyebrow>Accompagnement</Eyebrow>
                            <h2 className="text-[28px] md:text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] mb-4">Une équipe qui code ce qui vous manque.</h2>
                            <p className="text-[14.5px] text-content-muted leading-relaxed">Un outil vous manque ? Vous l'écrivez depuis le bouton support de votre espace. Une vraie personne répond, et la fonctionnalité est développée pour votre établissement.</p>
                        </div>
                        <ul className="divide-y divide-border self-center">
                            {ACCOMPAGNEMENT.map((a) => (
                                <li key={a.label} className="flex flex-wrap items-baseline justify-between gap-3 py-3.5">
                                    <span className="text-[14px] font-semibold">{a.label}</span>
                                    <span className="text-[12.5px] text-content-muted">{a.note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="max-w-[1160px] mx-auto px-5 pb-20 text-center">
                    <h2 className="text-[30px] md:text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] mb-3">On configure votre espace avec vous.</h2>
                    <p className="mx-auto max-w-[460px] text-[14.5px] text-content-muted mb-7">Une démo de 30 minutes, vos modules choisis ensemble, votre équipe formée.</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {demoButton('lg')}
                        <Pill variant="secondary" size="lg" to="/login">Se connecter</Pill>
                    </div>
                </section>
            </div>
        </>
    );
};

/** Vitrine publique (« / » hors connexion) : l'accueil du site, qui renvoie vers une page par métier. */
export const PublicHomepage: React.FC = () => (
    <VitrineLayout title="L'IAmani — Activez vos modules, on code le reste.">
        <Accueil />
    </VitrineLayout>
);

export default PublicHomepage;
